import { Component, Emit, Inject, Prop, Ref, Watch } from 'vue-property-decorator';
import { required } from 'vuelidate/lib/validators';
import ApeironComponent from '@/components/commons/apeiron-component.model';
import { Solucion } from '@/shared/model/solucion.model';
import { Solicitud } from '@/shared/model/solicitud.model';
import { mixins } from 'vue-class-component';
import { ProgramaIntegrado, ProgramaIntegradoSeleccionable } from '@/shared/model/snp/programa-integrado.model';
import SolicitudService from '@/entities/solicitud/solicitud.service';
import { FiltroPrograma } from '@/shared/model/filtro-programa.model';
import { Programa } from '@/shared/model/programa.model';
import ProgramaService from '@/shared/programa/programa.service';
import { BModal } from 'bootstrap-vue';
import { Area } from '@/shared/model/enumerations/area';

const validations = function () {
  const { currentProgramaIngregado } = this as ProgramaIntegradoConRegistroComponent;
  return {
    currentProgramaIngregado: {
      fechaInicio: { required },
      fechaFin: {
        minValue: val => {
          return val != undefined && currentProgramaIngregado.fechaInicio != undefined ? val >= currentProgramaIngregado.fechaInicio : true;
        },
      },
    },
  };
};
@Component({
  validations,
})
export default class ProgramaIntegradoConRegistroComponent extends mixins(ApeironComponent) {
  @Inject('programaService') private programaService: () => ProgramaService;
  @Inject('solicitudService') private solicitudService: () => SolicitudService;

  @Ref('addIntegrado')
  public readonly programaIntegradoModal: BModal;

  @Prop({ required: true })
  public solucion: Solucion;

  @Prop({ required: true })
  public solicitud: Solicitud;

  @Prop({ required: true })
  public estaVisible: boolean;

  public currentProgramaIngregado: ProgramaIntegrado = new ProgramaIntegrado();
  private solicitudConsultada: Solicitud = new Solicitud();

  public isFetching = false;
  public currentPage = 1;
  public perPage = 10;
  public totalRows = 0;
  public perPageOptions = [10, 20, 50, 100, 150];

  public filtroPrograma = '';
  public filtroClave = '';
  public filtro = new FiltroPrograma();
  public programas: Array<ProgramaIntegradoSeleccionable> = [];
  public estaIntegrado = false;

  public programasFields = [
    {
      key: 'programa.clave',
      label: this.$t('apeironGwApp.programaIntegradoCon.registro.clave'),
      class: 'text-left font-weight-bold',
      thStyle: { width: '6rem' },
    },
    {
      key: 'programa.nombre',
      label: this.$t('apeironGwApp.programaIntegradoCon.registro.nombre'),
      class: 'text-left',
      thStyle: { width: '30rem' },
    },
    {
      key: 'integrar',
      label: this.$t('apeironGwApp.programaIntegradoCon.registro.integrar'),
      class: 'text-center',
      thStyle: { width: '6rem' },
    },
  ];

  public mounted(): void {
    this.iniciarComponente();
  }

  @Watch('estaVisible')
  public watchEstaVisible(): void {
    if (this.estaVisible) {
      this.iniciarComponente();
    }
  }

  public iniciarComponente(): void {
    this.currentProgramaIngregado = new ProgramaIntegrado();

    this.solicitudService()
      .find(this.solicitud.id)
      .then(solicitud => {
        this.solicitudConsultada = solicitud;

        (this.solicitudConsultada?.programa?.instituciones ?? []).forEach(institucion => {
          (institucion?.sedes ?? []).forEach(sede => {
            if (sede.postulante) {
              this.filtro.claveInstitucion = institucion.claveInstitucion;
            }
          });
        });

        this.handlePageSizeChange();
      })
      .catch(error => {
        this.alertService().showHttpError(this, error.response);
      });
  }

  public guardarProgramaIntegrado(): void {
    if (!this.readOnly) {
      if (!this.formularioIsInvalid) {
        this.solicitudService()
          .find(this.solicitud.id)
          .then(solicitud => {
            if (solicitud.programasIntegrados?.length > 1) {
              this.alertService().showWarning(this, this.$t('apeironGwApp.programaIntegradoCon.registro.yaIntegrado').toString());
              return;
            }

            solicitud.programasIntegrados = (solicitud.programasIntegrados ?? []).filter(
              programa => programa.clave !== this.currentProgramaIngregado.clave
            );
            solicitud.programasIntegrados.push(this.currentProgramaIngregado);

            this.solicitudService()
              .update(solicitud)
              .then(() => {
                this.agregarProgramaIntegrado();
              })
              .catch(error => {
                this.alertService().showHttpError(this, error.response);
              });
          })
          .catch(error => {
            this.alertService().showHttpError(this, error.response);
          });

        this.programaIntegradoModal.hide();
      } else {
        this.alertService().showWarning(this, this.$t('apeironGwApp.programaIntegradoCon.registro.formularioInvalido').toString());
      }
    }
  }

  @Emit('agregarProgramaIntegrado')
  public agregarProgramaIntegrado(): ProgramaIntegrado {
    return { ...this.currentProgramaIngregado };
  }

  /**
   * Obtiene los programas del filtro de búsqueda de forma paginada
   */
  private retrieveProgramas() {
    if (!this.filtro.claveInstitucion) return;

    if (this.solicitud.programa?.cveGrado === 'MAE') {
      this.filtro.cveGrado = 'DOC';
    } else if (this.solicitud.programa?.cveGrado === 'DOC') {
      this.filtro.cveGrado = 'MAE';
    } else {
      return;
    }
    this.isFetching = true;
    this.filtro.nombrePrograma = this.filtroPrograma;
    this.filtro.clave = this.filtroClave;
    this.filtro.area = this.solucion?.params?.area ?? Area.POSGRADOS;
    if (this.solicitudConsultada.programasIntegrados?.filter(programa => programa.clave !== undefined).length > 0) {
      this.estaIntegrado = true;
    }
    const paginationQuery = {
      page: this.currentPage - 1,
      size: this.perPage,
      sort: ['id,desc'],
      filter: this.filtro,
    };

    this.programaService()
      .retrieve(paginationQuery)
      .then(res => {
        this.programas = [];
        const programasIntegrados = this.solicitudConsultada.programasIntegrados ?? [];

        (res.data ?? []).forEach(programa => {
          const programaObj = Programa.fromObject(programa);
          const disponible = !programasIntegrados.find(programaIntegrado => programaIntegrado.clave === programaObj.clave);
          const esEstePrograma = this.solicitudConsultada.programa?.clave == programaObj.clave;

          this.programas.push(new ProgramaIntegradoSeleccionable(programaObj, disponible, esEstePrograma));
        });
        this.totalRows = Number(res.headers['x-total-count']);
      })
      .catch(error => {
        this.alertService().showHttpError(this, error.response);
      })
      .finally(() => {
        this.isFetching = false;
      });
  }

  /**
   * Actualiza los programas de la página recibida
   * @param pageNumber página a actualizar
   */
  public handlePageChange(pageNumber: number): void {
    this.currentPage = pageNumber;
    this.retrieveProgramas();
  }

  /**
   * Actualiza los programas en base al nuevo tamaño de página
   */
  public handlePageSizeChange(): void {
    this.resetPagination();
    this.retrieveProgramas();
  }

  /**
   * Reinicia el número de página y el total de filas
   */
  private resetPagination(): void {
    this.currentPage = 1;
    this.totalRows = 0;
  }

  /**
   *Muestra el modal para agregar un programa integrado
   * @param programaSeleccionable a integrar
   */
  public handleIntegrarPrograma(programaSeleccionable) {
    this.currentProgramaIngregado = new ProgramaIntegrado();
    this.currentProgramaIngregado.clave = programaSeleccionable.programa.clave;
    this.currentProgramaIngregado.nombre = programaSeleccionable.programa.nombre;
    this.programaIntegradoModal.show();
  }

  /**
   * Cierra el modal de programa integrado
   */
  public handleCloseIntegrado(): void {
    this.programaIntegradoModal.hide();
  }

  public limpiarFiltros(): void {
    this.filtroPrograma = '';
    this.filtroClave = '';
    this.handlePageSizeChange();
  }

  get formularioIsInvalid(): boolean {
    return this.$v.$invalid;
  }

  /**
   * Indicador para permitir el modo escritura
   */
  get esEscritura(): boolean {
    return this.esEscrituraConEstadoEnCaptura(this.solicitud.estado);
  }
}
